import { useMutation, useQueryClient } from '@tanstack/react-query';

/* APIS */
import { cart } from '@api/apis';

/* Keys */
import { cartQueryKeys } from './queries.keys';

/* Notification Store */
import { NoticeTypes, updateAppNotification } from '@store/client/store.notifications';

/* hooks */
import useNotice from '@hooks/useNotice';

/* utils */
import { Logger } from '@utils/logger';

/* Tracking */
import { SegmentTrackingAction } from '@integrations/segment/sku';

/* types */
import { AddSkuStatePayload } from '@scenes/AddSkuPage/store/store.types';
import { Sku } from 'types/sku.types';
import { deriveResponseError } from '@utils/object';

type UseCartAddToCartProp = {
  email?: string | null;
  item: AddSkuStatePayload;
  sku: Sku;
  errorType?: NoticeTypes;
  manualCartToken?: string;
};

export default function useCartMutationAddToCart({ errorType = NoticeTypes.MODAL_NOTICE }: Pick<UseCartAddToCartProp, 'errorType'> = {}) {
  const queryClient = useQueryClient();
  const { error } = useNotice();

  return useMutation({
    mutationFn: async ({ item, email, sku, manualCartToken }: UseCartAddToCartProp) => {
      const data = await cart.addItem({ item, email, ...(manualCartToken && { manualCartToken }) });
      if (data.status === 500 || data.err) throw data;

      return data;
    },
    onSuccess: (data, args: UseCartAddToCartProp) => {
      const { cart: cartResponse = {} } = data?.data ?? {};
      queryClient.setQueryData(cartQueryKeys.all, { ...cartResponse });

      SegmentTrackingAction.skuAddedToCart({ cart: cartResponse, sku: args.sku });
    },
    onError: data => {
      /* Do we want a snack or modal errorType */
      if (errorType === NoticeTypes.MODAL_NOTICE) {
        const { errors = 'Could not add your selection.', error_title: errorTitle = 'Sku Error' } = deriveResponseError(data);

        updateAppNotification({ error_title: errorTitle, errors });
      } else {
        error('Error! Could not add your selection.');
      }

      Logger('useCartAddToCart')(data as Error);
    },
  });
}
